import { HttpClient } from '@angular/common/http';
import {
  LogService,
  OfflineStorageService,
  WindowService,
} from '@ups/xplat/core';
import { StorageKeys } from '@ups/xplat/utils';
import { of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { IDynamicContainerMetadata, IDynamicOfflineCache } from '../models';
import { isOfflineCacheStale } from './offline-prefetch.utils';

const logTag = 'DynamicRenderService';
const logTagPrefetchDropdowns = 'prefectched dropdown data:';

/** Collaborators read lazily so specs can swap them on the owning service. */
export interface IDynamicOfflineCacheDeps {
  http: () => HttpClient;
  offlineStorage: () => OfflineStorageService;
  log: () => LogService;
  win: () => WindowService;
}

/**
 * Offline caching of dynamic containers and the consolidated dropdown prefetch,
 * split out of DynamicRenderService (Bug 34694) so the prefetch can be shared
 * across concurrent callers without growing that service.
 */
export class DynamicOfflineCache {
  /** True once a dropdown cache is stored or confirmed fresh this session. */
  hasCachedData = false;
  private dropdownsPrefetch?: Promise<IDynamicOfflineCache>;

  constructor(private readonly deps: IDynamicOfflineCacheDeps) {}

  private get http() {
    return this.deps.http();
  }
  private get offlineStorage() {
    return this.deps.offlineStorage();
  }
  private get log() {
    return this.deps.log();
  }
  private get win() {
    return this.deps.win();
  }

  cacheContainers(data: Array<IDynamicContainerMetadata>) {
    return new Promise<void>((resolve) => {
      const dynamicCache: IDynamicOfflineCache = {
        cacheDate: Date.now(),
        data,
      };
      this.offlineStorage
        .setItem(
          StorageKeys.OFFLINE_DATA_DYNAMIC_CONTAINERS,
          {
            dynamicContainers: dynamicCache,
          },
          'dynamicContainers'
        )
        .pipe(take(1))
        .subscribe(() => {
          const cacheDate = dynamicCache.cacheDate || Date.now();

          this.log.debug(
            logTag,
            'all dynamic container data:',
            'Cache refreshed, stored and dated:',
            new Date(cacheDate)
          );
          resolve();
        });
    });
  }

  /**
   * Prefetches dropdown data and stores for offline use later
   * @returns Promise when prefeth and cache is complete
   */
  prefetchDropdowns(forceRefresh?: boolean): Promise<IDynamicOfflineCache> {
    // Bug 34694: one prefetch per session, shared by concurrent callers. A
    // boolean set in the response callback let overlapping callers each pay
    // the /api/Form/dropdowns fan-out (Sparta + 4 Viewpoint calls).
    if (this.dropdownsPrefetch && !forceRefresh) {
      return this.dropdownsPrefetch;
    }
    const prefetch = new Promise<IDynamicOfflineCache>((resolve) => {
      const fetchAndCache = () => {
        this.log.debug(
          logTag,
          logTagPrefetchDropdowns,
          'Fetching fresh for offline use later...'
        );
        this.http
          .get<Partial<IDynamicOfflineCache>>(`/api/Form/dropdowns`)
          .pipe(
            catchError((err) => {
              this.log.debug(logTag, 'ERROR:', err);
              return of<Partial<IDynamicOfflineCache> | null>(null);
            })
          )
          .subscribe((data) => {
            const dynamicCache: IDynamicOfflineCache = {
              cacheDate: Date.now(),
              ...(data || {}),
            };
            if (!data) {
              // Bug 34694: never store (or remember) a failed fetch. An empty
              // cache dated now used to stick, so the next call retries.
              // Deferred a microtask: a synchronous failure lands here before
              // `prefetch` is assigned.
              Promise.resolve().then(() => {
                if (this.dropdownsPrefetch === prefetch) {
                  this.dropdownsPrefetch = undefined;
                }
              });
              resolve(dynamicCache);
              return;
            }
            if (this.win.navigator.onLine) {
              // only cache data when online to ensure only fresh data is stored
              this.offlineStorage
                .setItem(
                  StorageKeys.OFFLINE_DATA_DROPDOWNS,
                  {
                    dynamicDropdowns: dynamicCache,
                  },
                  'dynamicDropdowns'
                )
                .pipe(take(1))
                .subscribe();
              const cacheDate = dynamicCache.cacheDate || Date.now();

              this.log.debug(
                logTag,
                logTagPrefetchDropdowns,
                'Cache refreshed, stored and dated:',
                new Date(cacheDate)
              );
            }
            this.hasCachedData = true;
            resolve(dynamicCache);
          });
      };
      if (forceRefresh) {
        this.log.debug(logTag, logTagPrefetchDropdowns, 'refreshing cache...');
        fetchAndCache();
      } else {
        this.log.debug(
          logTag,
          logTagPrefetchDropdowns,
          'checking for existing cache...'
        );
        this.offlineStorage
          .getItem(StorageKeys.OFFLINE_DATA_DROPDOWNS, null, 'dynamicDropdowns')
          .pipe(take(1))
          .subscribe((data: { dynamicDropdowns?: IDynamicOfflineCache }) => {
            if (data?.dynamicDropdowns) {
              const cacheDate = data.dynamicDropdowns.cacheDate || Date.now();

              // check if out of date
              // right now, we will cache every 2 weeks
              // TODO: can add a user setting that any user could initiate a fresh cache reset to force preload a fresh cache on demand
              if (isOfflineCacheStale(cacheDate)) {
                // refetch and cache
                this.log.debug(
                  logTag,
                  logTagPrefetchDropdowns,
                  'cache found but was out of date:',
                  new Date(cacheDate),
                  '...fetching fresh data...'
                );
                fetchAndCache();
              } else {
                // utlize existing cache
                this.log.debug(
                  logTag,
                  logTagPrefetchDropdowns,
                  'cache found dated:',
                  new Date(cacheDate)
                );
                this.hasCachedData = true;
                resolve(data.dynamicDropdowns);
              }
            } else {
              fetchAndCache();
            }
          });
      }
    });
    this.dropdownsPrefetch = prefetch;
    return prefetch;
  }
}
